import { redirect, type RouteObject } from 'react-router';

import {
  SoftwareEscrowOnboardingNew,
  SoftwareEscrowOnboardingMembers,
  SoftwareEscrowOnboardingDepositor,
  SoftwareEscrowOnboardingBeneficiary,
  SoftwareEscrowOnboardingRoutesEnum,
  SoftwareEscrowOnboardingNotificationSettings,
  SoftwareEscrowOnboardingActivationNotifications,
  SoftwareEscrowOnboardingAssetVisibility,
  SoftwareEscrowOnboardingStorageSettings,
  SoftwareEscrowOnboardingAgreementSetup,
  SoftwareEscrowOnboardingAgreementDetails,
  SoftwareEscrowOnboardingSummary,
} from 'src/pages/SoftwareEscrowOnboarding';
import { Layout as OnboardingLayout } from 'src/pages/SoftwareEscrowOnboarding/ui';
import {
  SoftwareEscrowOverview,
  SoftwareEscrowLayout,
  SoftwareEscrowAgreement,
  SoftwareEscrowDeposits,
  SettingsAgreement,
  SettingsLayout,
  SettingsBeneficiary,
  SettingsDepositor,
  SettingsTechnical,
  SoftwareEscrowRoutesEnum,
  SoftwareEscrowSettingsRoutesEnum,
  SoftwareEscrowMembers,
  SoftwareEscrowReports,
} from 'src/pages/SoftwareEscrow';
import {
  SoftwareEscrowRequestChangesAgreement,
  SoftwareEscrowRequestChangesBeneficiary,
  SoftwareEscrowRequestChangesDepositor,
  SoftwareEscrowRequestChangesTechnical,
  SoftwareEscrowRequestChangesRoutesEnum,
  SoftwareEscrowRequestChangesLayout,
  SoftwareEscrowRequestChangesDeposits,
} from 'src/pages/SoftwareEscrowRequestChanges';
import SoftwareEscrows from 'src/pages/SoftwareEscrows';

export const softwareEscrowsRoutes: RouteObject = {
  path: 'software-escrows',
  element: <SoftwareEscrows />,
  children: [
    {
      path: 'invitations/:invitationId',
      lazy: async () => {
        const { InvitationDialog: Component } = await import('src/pages/SoftwareEscrows/ui');

        return { Component };
      },
    },
  ],
};

export const softwareEscrowsInvitationRoute: RouteObject = {
  path: 'software-escrows/invitations/:invitationId',
  element: <SoftwareEscrows />,
};

export const softwareEscrowsOnboardingRoutes: RouteObject = {
  path: 'software-escrows/onboarding',
  children: [
    {
      path: 'new',
      element: <SoftwareEscrowOnboardingNew />,
    },
    {
      path: ':id/*',
      element: <OnboardingLayout />,
      children: [
        {
          path: SoftwareEscrowOnboardingRoutesEnum.Members,
          element: <SoftwareEscrowOnboardingMembers />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.Depositor,
          element: <SoftwareEscrowOnboardingDepositor />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.Beneficiary,
          element: <SoftwareEscrowOnboardingBeneficiary />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.NotificationSettings,
          element: <SoftwareEscrowOnboardingNotificationSettings />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.ActivationNotifications,
          element: <SoftwareEscrowOnboardingActivationNotifications />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.AssetVisibility,
          element: <SoftwareEscrowOnboardingAssetVisibility />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.StorageSettings,
          element: <SoftwareEscrowOnboardingStorageSettings />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.AgreementSetup,
          element: <SoftwareEscrowOnboardingAgreementSetup />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.AgreementDetails,
          element: <SoftwareEscrowOnboardingAgreementDetails />,
        },
        {
          path: SoftwareEscrowOnboardingRoutesEnum.Summary,
          element: <SoftwareEscrowOnboardingSummary />,
        },
      ],
    },
  ],
};

export const softwareEscrowRoutes: RouteObject = {
  path: 'software-escrows/:id/*',
  element: <SoftwareEscrowLayout />,
  loader: ({ params }) => {
    const { id, '*': path = '' } = params;

    if (!path) return redirect(`/software-escrows/${id}/${SoftwareEscrowRoutesEnum.Overview}`);

    if (
      !Object.values(SoftwareEscrowRoutesEnum).includes(path as SoftwareEscrowRoutesEnum) &&
      !Object.values(SoftwareEscrowSettingsRoutesEnum).includes(path as SoftwareEscrowSettingsRoutesEnum)
    )
      return redirect('/404');

    return null;
  },
  children: [
    {
      path: SoftwareEscrowRoutesEnum.Overview,
      element: <SoftwareEscrowOverview />,
    },
    {
      path: SoftwareEscrowRoutesEnum.Agreement,
      element: <SoftwareEscrowAgreement />,
    },
    {
      path: SoftwareEscrowRoutesEnum.Deposits,
      element: <SoftwareEscrowDeposits />,
    },
    {
      path: `${SoftwareEscrowRoutesEnum.Settings}/*`,
      element: <SettingsLayout />,
      loader: ({ params }) => {
        const { id, '*': path = '' } = params;

        const defaultLink = `/software-escrows/${id}/${SoftwareEscrowRoutesEnum.Settings}/${SoftwareEscrowSettingsRoutesEnum.Depositor}`;
        const isValidPath = Object.values(SoftwareEscrowSettingsRoutesEnum).includes(
          path as SoftwareEscrowSettingsRoutesEnum,
        );

        if (!path || !isValidPath) return redirect(defaultLink);

        return null;
      },
      children: [
        {
          path: SoftwareEscrowSettingsRoutesEnum.Agreement,
          element: <SettingsAgreement />,
        },
        {
          path: SoftwareEscrowSettingsRoutesEnum.Beneficiary,
          element: <SettingsBeneficiary />,
        },
        {
          path: SoftwareEscrowSettingsRoutesEnum.Depositor,
          element: <SettingsDepositor />,
        },
        {
          path: SoftwareEscrowSettingsRoutesEnum.Technical,
          element: <SettingsTechnical />,
        },
      ],
    },
    {
      path: SoftwareEscrowRoutesEnum.Members,
      element: <SoftwareEscrowMembers />,
    },
    {
      path: SoftwareEscrowRoutesEnum.Reports,
      element: <SoftwareEscrowReports />,
    },
  ],
};

export const softwareEscrowsRequestChangesRoutes: RouteObject = {
  path: 'software-escrows/:id/request-changes/*',
  element: <SoftwareEscrowRequestChangesLayout />,
  children: [
    {
      path: SoftwareEscrowRequestChangesRoutesEnum.Agreement,
      element: <SoftwareEscrowRequestChangesAgreement />,
    },
    {
      path: SoftwareEscrowRequestChangesRoutesEnum.Beneficiary,
      element: <SoftwareEscrowRequestChangesBeneficiary />,
    },
    {
      path: SoftwareEscrowRequestChangesRoutesEnum.Depositor,
      element: <SoftwareEscrowRequestChangesDepositor />,
    },
    {
      path: SoftwareEscrowRequestChangesRoutesEnum.Technical,
      element: <SoftwareEscrowRequestChangesTechnical />,
    },
    {
      path: SoftwareEscrowRequestChangesRoutesEnum.Deposits,
      element: <SoftwareEscrowRequestChangesDeposits />,
    },
  ],
};
